// i18n area 'explorer' (NAS-WebExplorer #30, 2026-08-05 — REAL): the
// OneDrive/Drive-style file explorer over the NAS shares, wired to the live
// fs surface (list/upload/download/mkdir/rename/delete + local previews).
// The mock-skeleton keys of the first pass are gone with the mock.
// The nav label lives HERE (`explorer.nav`, the objectStorage.nav precedent):
// dictionaries.ts composes sections with a shallow spread, so a second `nav`
// branch would clobber common.ts instead of merging into it.

export const en = {
  explorer: {
    nav: 'File Explorer',
    title: 'File Explorer',
    share: 'Share',
    shareHint: 'Pick the share to browse — served through its serving node, wherever it lives',
    noShares: 'No serving file share exists yet — the explorer roots at a ready (or degraded) share.',
    noSharesKiosk: 'No share is assigned to your account yet — ask your administrator.',
    readOnlyShare: 'Read-only',
    root: 'Root',
    search: 'Filter this folder…',
    viewList: 'List view',
    viewGrid: 'Grid view',
    empty: 'This folder is empty',
    noMatch: 'Nothing here matches the filter',
    name: 'Name',
    size: 'Size',
    modified: 'Modified',
    folder: 'Folder',
    itemsOne: '1 item',
    itemsMany: '{count} items',
    selectedCount: '{count} selected',
    loadMore: 'Load more',
    uploadFiles: 'Upload files',
    uploadFolder: 'Upload folder',
    uploadProgress: 'Uploading {done} of {total} — {name}',
    uploadFailed: '{count} upload(s) failed — the rest of the queue went through',
    newFolder: 'New folder',
    download: 'Download',
    downloadZip: 'Download as ZIP',
    downloadSelected: 'Download selected',

    // #30c (dictations Luca 2026-08-06): context menu + preview deep-link.
    open: 'Open',
    previewMissing: '{name} is not in this folder — it may have been renamed or removed.',
    preview: 'Preview',
    editorLoading: 'Opening the editor…',
    noPreview: 'No preview for this file type — download it to open it locally.',
    previewTruncated: 'Preview truncated to the first 1 MiB — download the file for the full content.',
    prevPage: 'Previous page',
    nextPage: 'Next page',
    pageOf: 'Page {page} of {pages}',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomReset: 'Reset zoom',
    viewRendered: 'Rendered view',
    viewSource: 'Source view',
    wrapLines: 'Wrap long lines',
    rename: 'Rename',
    delete: 'Delete',
    deleteSelected: 'Delete selected',
    newFolderTitle: 'New folder',
    folderName: 'Folder name',
    renameTitle: 'Rename {name}',
    newName: 'New name',
    deleteTitle: 'Delete {count} item(s)?',
    deleteWarning:
      'Folders fall RECURSIVELY with everything inside. There is no trash and no undo — the bytes are gone from the share.',
    invalidName: 'Letters, digits, dots, dashes, underscores and spaces — no slash',
    duplicateName: 'An entry with this name already exists in this folder',

    // WEBEXP-EDIT #63 (2026-08-05, ratifica Luca — Collabora/CODE via WOPI):
    // the office-editing gesture and its speaking refusals.
    openEditor: 'Open in editor',
    openEditorOnline: 'Open in the online editor',
    editorCloseTitle: 'Close the editor?',
    editorCloseWarning:
      'The document closes with the frame. Collabora saves your changes as you work, but a save still in flight may be lost — make sure the editor shows the document as saved before closing.',
    editorUnconfiguredTitle: 'Document editor not configured',
    editorUnconfigured:
      'Office editing needs a Collabora Online server configured at cluster level. An administrator can set it under Cluster → Settings → Document editor (sudo required).',
    editorGoCluster: 'Open cluster settings'
  }
}

export const it: typeof en = {
  explorer: {
    nav: 'Esplora File',
    title: 'Esplora File',
    share: 'Share',
    shareHint: 'Scegli la share da esplorare — servita attraverso il suo nodo di servizio, ovunque sia',
    noShares: 'Non esiste ancora nessuna file share in servizio — l\'explorer parte da una share ready (o degraded).',
    noSharesKiosk: 'Nessuna share è ancora assegnata al tuo account — chiedi al tuo amministratore.',
    readOnlyShare: 'Sola lettura',
    root: 'Radice',
    search: 'Filtra questa cartella…',
    viewList: 'Vista elenco',
    viewGrid: 'Vista griglia',
    empty: 'Questa cartella è vuota',
    noMatch: 'Nessun elemento corrisponde al filtro',
    name: 'Nome',
    size: 'Dimensione',
    modified: 'Modificato',
    folder: 'Cartella',
    itemsOne: '1 elemento',
    itemsMany: '{count} elementi',
    selectedCount: '{count} selezionati',
    loadMore: 'Carica altri',
    uploadFiles: 'Carica file',
    uploadFolder: 'Carica cartella',
    uploadProgress: 'Caricamento {done} di {total} — {name}',
    uploadFailed: '{count} caricamenti falliti — il resto della coda è andato a buon fine',
    newFolder: 'Nuova cartella',
    download: 'Scarica',
    downloadZip: 'Scarica come ZIP',
    downloadSelected: 'Scarica selezionati',

    // #30c (dettature Luca 2026-08-06): menù contestuale + deep-link anteprima.
    open: 'Apri',
    previewMissing: '{name} non è in questa cartella — potrebbe essere stato rinominato o rimosso.',
    preview: 'Anteprima',
    editorLoading: 'Apertura dell\'editor…',
    noPreview: 'Nessuna anteprima per questo tipo di file — scaricalo per aprirlo in locale.',
    previewTruncated: 'Anteprima troncata al primo MiB — scarica il file per il contenuto completo.',
    prevPage: 'Pagina precedente',
    nextPage: 'Pagina successiva',
    pageOf: 'Pagina {page} di {pages}',
    zoomIn: 'Ingrandisci',
    zoomOut: 'Riduci',
    zoomReset: 'Reimposta lo zoom',
    viewRendered: 'Vista renderizzata',
    viewSource: 'Vista sorgente',
    wrapLines: 'Manda a capo le righe lunghe',
    rename: 'Rinomina',
    delete: 'Elimina',
    deleteSelected: 'Elimina selezionati',
    newFolderTitle: 'Nuova cartella',
    folderName: 'Nome della cartella',
    renameTitle: 'Rinomina {name}',
    newName: 'Nuovo nome',
    deleteTitle: 'Eliminare {count} elemento/i?',
    deleteWarning:
      'Le cartelle cadono RICORSIVAMENTE con tutto il contenuto. Non esiste cestino né annulla — i byte spariscono dalla share.',
    invalidName: 'Lettere, cifre, punti, trattini, underscore e spazi — niente slash',
    duplicateName: 'In questa cartella esiste già una voce con questo nome',

    // WEBEXP-EDIT #63 (2026-08-05, ratifica Luca — Collabora/CODE via WOPI):
    // il gesto di editing office e i suoi rifiuti parlanti.
    openEditor: 'Apri nell\'editor',
    openEditorOnline: 'Apri nell\'editor online',
    editorCloseTitle: 'Chiudere l\'editor?',
    editorCloseWarning:
      'Il documento si chiude con la finestra. Collabora salva le modifiche mentre lavori, ma un salvataggio ancora in corso può andare perso — assicurati che l\'editor mostri il documento come salvato prima di chiudere.',
    editorUnconfiguredTitle: 'Editor di documenti non configurato',
    editorUnconfigured:
      'L\'editing office richiede un server Collabora Online configurato a livello di cluster. Un amministratore può impostarlo in Cluster → Impostazioni → Editor di documenti (serve sudo).',
    editorGoCluster: 'Apri le impostazioni del cluster'
  }
}
